import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Crown, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const PricingPage = () => {
  const [packages, setPackages] = useState({ subscriptions: [], boosts: [] });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const { axiosInstance, user } = useAuth();
  const navigate = useNavigate();


  const fetchPackages = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/packages');
      setPackages(res.data);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleSubscribe = async (packageId) => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    setProcessingId(packageId);
    try {
      const res = await axiosInstance.post('/payments/checkout/subscription', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setProcessingId(null);
    }
  };

  const premiumFeatures = [
    'Ad-free experience',
    'Unlimited video uploads',
    'HD video quality',
    'Priority support',
    'Early access to new features',
    'Exclusive creator badge'
  ];

  return (
    <div className="pb-32 px-6 max-w-5xl mx-auto">
      <div className="py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/60 border border-white/10 mb-6"
          >
            <Crown className="w-5 h-5 text-yellow-400" />
            <span className="text-sm font-medium text-white">Premium Membership</span>
          </motion.div>
          
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="gradient-text">Upgrade to Premium</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Unlock the full AUDIVUE experience with premium features
          </p>
        </div>

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {packages.subscriptions.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-zinc-900 border rounded-2xl overflow-hidden ${
                pkg.id === 'premium_yearly' 
                  ? 'border-white/30 shadow-lg' 
                  : 'border-zinc-800'
              }`}
            >
              {pkg.id === 'premium_yearly' && (
                <div className="absolute top-0 right-0 px-4 py-1 bg-white text-black text-sm font-medium">
                  Best Value
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-xl font-semibold text-white mb-2">{pkg.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold text-white">${pkg.amount}</span>
                  <span className="text-zinc-500">
                    /{pkg.id === 'premium_yearly' ? 'year' : 'month'}
                  </span>
                </div>
                
                {pkg.id === 'premium_yearly' && (
                  <div className="mb-6 px-3 py-1.5 bg-green-500/20 rounded-lg inline-block">
                    <span className="text-green-400 text-sm font-medium">Save $20/year</span>
                  </div>
                )}

                <ul className="space-y-3 mb-8">
                  {premiumFeatures.map((feature, i) => (
                    <li key={`feature-${i}`} className="flex items-center gap-3 text-zinc-300">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(pkg.id)}
                  disabled={processingId === pkg.id}
                  className={`w-full py-3 rounded-full font-semibold ${
                    pkg.id === 'premium_yearly'
                      ? 'bg-white text-black hover:opacity-90'
                      : 'bg-zinc-800 text-white hover:bg-zinc-700'
                  }`}
                  data-testid={`subscribe-${pkg.id}`}
                >
                  {processingId === pkg.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Get Premium'
                  )}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');


  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axiosInstance.get(`/payments/status/${sessionId}`);
      setPaymentInfo(res.data);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (err) {
      setStatus('error');
    }
  }, [axiosInstance, sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center"
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-white animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-zinc-400">Please wait while we confirm your payment...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-zinc-400 mb-6">
              {paymentInfo?.package_name} - ${paymentInfo?.amount}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-white text-black rounded-full px-8"
            >
              Continue to AUDIVUE
            </Button>
          </>
        )}
        
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
            <p className="text-zinc-400 mb-6">
              {status === 'timeout' 
                ? 'Payment verification timed out. Check your email for confirmation.'
                : 'There was an issue with your payment.'}
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-zinc-700 text-white rounded-full"
            >
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const PaymentCancelPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center"
      >
        <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl">🚫</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Payment Cancelled</h2>
        <p className="text-zinc-400 mb-6">
          Your payment was cancelled. No charges were made.
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-white text-black rounded-full"
          >
            View Plans
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-zinc-700 text-white rounded-full"
          >
            Go Home
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
